import { Component, EventEmitter, Output, ChangeDetectorRef, OnInit, ViewChild, OnDestroy, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EfileAdminService } from '../efile-admin.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-properties',
    templateUrl: './efile-properties.component.html',
    styleUrls: ['./efile-properties.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfilePropertiesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  showFormLoader = true;

  title = 'Add/Edit Efile Properties';
  minimise = false;
  saving = false;

  @ViewChild('dataServiceForm') dataServiceForm!: NgForm;

  constructor(
    private efileAdminService: EfileAdminService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    const row = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.rowData = { ...row };
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    this.showFormLoader = false;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {
    if (this.saving) return;
    if (this.dataServiceForm?.invalid) {
      this.gtwSnackbarService.openSnackBar('Please fill all required fields.', 'gtw-snackbar--red');
      return;
    }
    this.saving = true;
    this.cdr.detectChanges();

    this.efileAdminService.saveJsonObject(
      this.GENERAL_CONFIG?.base_url ?? '',
      32869,
      this.rowData,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.saving = false;
        this.cdr.detectChanges();
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = (data?.errorMessage && data.errorMessage !== 'null') ? data.errorMessage : 'Error saving.';
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.saving = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    });
  }
}
